import { type Locale } from "date-fns";
import { enCA } from "date-fns/locale/en-CA";
import { frCA } from "date-fns/locale/fr-CA";
import Cookies from "js-cookie";

/**
 * Locale map for date formatting
 * Maps locale codes from NEXT_LOCALE cookie to date-fns locale objects
 */
const LOCALE_MAP: Record<string, Locale> = {
  en: enCA,
  fr: frCA,
};

let dateLocaleOverride: string | null = null;

/**
 * Sets the locale used for date formatting when not in a browser with NEXT_LOCALE cookie.
 * Used by React Native so dates respect the app language (e.g. French).
 * On web, NEXT_LOCALE cookie takes precedence when set.
 *
 * **Mobile-only:** Must not be called from Next.js server code (e.g. getServerSideProps,
 * API routes, or Server Components). Use only in client or React Native.
 */
export const setDateLocaleOverride = (code: string | null): void => {
  dateLocaleOverride = code;
};

/**
 * Resolves the current date locale code (e.g. "en", "fr").
 * Used internally and by shared date helpers (e.g. months) that need the same locale source.
 */
export const getDateLocaleCode = (): string => {
  if (dateLocaleOverride !== null) return dateLocaleOverride;
  try {
    return Cookies.get("NEXT_LOCALE") || "en";
  } catch {
    return "en";
  }
};

/**
 * Returns the date-fns Locale object for the current date locale.
 * Use when formatting dates (e.g. formatInTimeZone, formatDistanceToNow) so output respects
 * the user's language (cookie on web, setDateLocaleOverride on mobile).
 */
export const getDateLocale = (): Locale => {
  const code = getDateLocaleCode();
  return LOCALE_MAP[code] ?? enCA;
};
